/**
 * WorkshopAuditLogModal — full-history paginated modal for the workshop audit
 * log (BON-201 redesign). Renders grouped AuditEventGroup blocks. Header shows
 * "{total} events · full history". Footer: "1–{end} of {total}" (left) +
 * "‹ Prev · Page {x}/{y} · Next ›" (right). Page size 20 events.
 */
import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import type { AuditEntry, WorkshopAuditPage } from '../../../../api/workshops';
import { fetchWorkshopAudit } from '../../../../api/workshops';
import { usePagination } from '../../../../hooks/usePagination';
import CloseButton from '../../../../shared/CloseButton';
import { PaginationFooter } from '../../../audit/PaginationFooter';

import { AuditEventGroup } from './AuditEventGroup';

const PAGE_SIZE = 20;

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(20,28,22,0.4)',
  padding: 24,
};

const panelStyle: CSSProperties = {
  width: 680,
  maxWidth: '95vw',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
};

interface Props {
  workshopId: string;
  onClose: () => void;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ModalHeader({ total, onClose }: { total: number; onClose: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px 12px',
        borderBottom: '1px solid var(--nb-border)',
      }}
    >
      <div>
        <h2
          className="nb-display"
          style={{ margin: 0, fontSize: 16, color: 'var(--nb-bark-dark)' }}
        >
          Audit log
        </h2>
        <div style={{ fontSize: 11, color: 'var(--nb-subtle)', marginTop: 2 }}>
          {total} events · full history
        </div>
      </div>
      <CloseButton onClick={onClose} />
    </div>
  );
}

function EventList({ events }: { events: AuditEntry[] }) {
  return (
    <div style={{ padding: '4px 16px' }}>
      {events.map((e) => (
        <AuditEventGroup key={e.id} entry={e} />
      ))}
    </div>
  );
}

// ─── Fetch hook ───────────────────────────────────────────────────────────────

type FetchState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; data: WorkshopAuditPage };

function useFetchAudit(workshopId: string, offset: number): FetchState {
  const [state, setState] = useState<FetchState>({ status: 'loading' });
  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetchWorkshopAudit(workshopId, { limit: PAGE_SIZE, offset })
      .then((data) => {
        if (!cancelled) setState({ status: 'ok', data });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load audit log.';
          setState({ status: 'error', message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workshopId, offset]);
  return state;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function WorkshopAuditLogModal({ workshopId, onClose }: Props) {
  // usePagination initialises with total=0; total updates once first fetch resolves.
  // We pass pagination.offset into useFetchAudit so page changes trigger re-fetches.
  const [fetchedTotal, setFetchedTotal] = useState(0);
  const pagination = usePagination(fetchedTotal, PAGE_SIZE);
  const fetchState = useFetchAudit(workshopId, pagination.offset);

  // Keep fetchedTotal in sync with the latest successful response so that
  // page math stays accurate while navigating.
  useEffect(() => {
    if (fetchState.status === 'ok') {
      setFetchedTotal(fetchState.data.total);
    }
  }, [fetchState]);

  const events = fetchState.status === 'ok' ? fetchState.data.events : [];
  const total = fetchedTotal;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Audit log"
      style={overlayStyle}
      onClick={onClose}
    >
      <div className="nb-card" style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <ModalHeader total={total} onClose={onClose} />

        <div style={{ flex: 1, overflow: 'auto' }}>
          {fetchState.status === 'loading' && (
            <div style={{ padding: 24, color: 'var(--nb-subtle)', fontSize: 12.5 }}>Loading…</div>
          )}
          {fetchState.status === 'error' && (
            <div style={{ padding: 24, color: 'var(--nb-danger, #dc2626)', fontSize: 12.5 }}>
              {fetchState.message}
            </div>
          )}
          {fetchState.status === 'ok' && total === 0 && (
            <div
              style={{ padding: 24, color: 'var(--nb-subtle)', fontSize: 12, fontStyle: 'italic' }}
            >
              No edits recorded yet.
            </div>
          )}
          {fetchState.status === 'ok' && total > 0 && <EventList events={events} />}
        </div>

        {fetchState.status === 'ok' && (
          <PaginationFooter
            offset={pagination.offset}
            total={total}
            count={events.length}
            currentPage={pagination.currentPage}
            pageCount={pagination.pageCount}
            hasPrev={pagination.hasPrev}
            hasNext={pagination.hasNext}
            onPrev={pagination.prevPage}
            onNext={pagination.nextPage}
          />
        )}
      </div>
    </div>
  );
}
